import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// Filters live here rather than in component state so the FilterDrawer and the
// table filter the SAME list. Keyword and filters COMPOSE — they are applied
// independently in useReportTypes, so applying both narrows rather than resets.
//
// ⚠️ Filtering is CLIENT-SIDE. `listReportTypes` takes no arguments at all, so
// there is no server-side filtering to prefer — but unlike the paginated
// modules, that costs nothing here: the query returns the WHOLE catalogue in
// one response, so client-side filtering sees every row. The usual "only the
// loaded window was examined" caveat does not apply to this module.
export type ReportTypesFilters = Record<string, string>;

let globalFilters: ReportTypesFilters = {};
const filterListeners: Set<(filters: ReportTypesFilters) => void> = new Set();

const notifyFilterChange = (filters: ReportTypesFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetReportTypesFilter = () => notifyFilterChange({});

export const useReportTypesFilter = () => {
    const [filters, setFilters] = useState<ReportTypesFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: ReportTypesFilters) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // The badge on FilterRecordsButton — a filter you cannot see is a filter you
    // forget is on (§7). Empty-string values are treated as unset so a cleared
    // field does not keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
